import { signIn, signUp } from '../firebase/auth.js';
import router from '../router.js';
import { toast } from '../components/Toast.js';
import { t, getLanguage, setLanguage } from '../i18n.js';

/**
 * Create login view
 * @returns {string} HTML string
 */
export function createLoginView() {
  const currentLang = getLanguage();
  const nextLang = currentLang === 'tr' ? 'en' : 'tr';
  const langToggleText = currentLang === 'tr' ? 'EN' : 'TR';

  return `
    <div class="page">
      <div class="container">
        <div class="login-container">
          <div class="login-card card">
            <div class="login-header">
              <div style="display: flex; justify-content: flex-end; margin-bottom: 1rem;">
                <button class="btn btn-ghost btn-sm login-lang-toggle" data-next-lang="${nextLang}">
                  🌐 ${langToggleText}
                </button>
              </div>
              <div class="login-logo">🏛️</div>
              <h1 class="login-title">${t('app.title')}</h1>
              <p class="login-subtitle">${t('login.subtitle')}</p>
            </div>
            
            <div class="tabs" id="auth-tabs">
              <button class="tab active" data-tab="signin">${t('login.button')}</button>
              <button class="tab" data-tab="signup">${t('nav.register') || 'Register'}</button>
            </div>
            
            <!-- Sign In Form -->
            <form id="signin-form" class="auth-form">
              <div class="form-group">
                <label class="form-label" for="signin-email">${t('login.email')}</label>
                <input 
                  type="email" 
                  id="signin-email" 
                  class="form-input" 
                  placeholder="isim@ornek.com"
                  required
                >
              </div>
              
              <div class="form-group">
                <label class="form-label" for="signin-password">${t('login.password')}</label>
                <input 
                  type="password" 
                  id="signin-password" 
                  class="form-input" 
                  placeholder="••••••••"
                  required
                  minlength="6"
                >
              </div>
              
              <button type="submit" class="btn btn-primary btn-lg" style="width: 100%">
                ${t('login.button')}
              </button>
            </form>
            
            <!-- Sign Up Form (hidden by default) -->
            <form id="signup-form" class="auth-form hidden">
              <div class="form-group">
                <label class="form-label" for="signup-name">${t('login.name') || 'Full Name'}</label>
                <input 
                  type="text" 
                  id="signup-name" 
                  class="form-input" 
                  placeholder="Dr. Ayşe Yılmaz"
                  required
                >
              </div>
              
              <div class="form-group">
                <label class="form-label" for="signup-email">${t('login.email')}</label>
                <input 
                  type="email" 
                  id="signup-email" 
                  class="form-input" 
                  placeholder="isim@ornek.com"
                  required
                >
              </div>
              
              <div class="form-group">
                <label class="form-label" for="signup-password">${t('login.password')}</label>
                <input 
                  type="password" 
                  id="signup-password" 
                  class="form-input" 
                  placeholder="••••••••"
                  required
                  minlength="6"
                >
                <span class="form-hint">${t('login.passwordHint') || 'Min 6 characters'}</span>
              </div>
              
              <div class="form-group">
                <label class="form-label" for="signup-confirm">${t('login.confirmPassword') || 'Confirm Password'}</label>
                <input 
                  type="password" 
                  id="signup-confirm" 
                  class="form-input" 
                  placeholder="••••••••"
                  required
                  minlength="6"
                >
              </div>
              
              <button type="submit" class="btn btn-primary btn-lg" style="width: 100%">
                ${t('login.register') || 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    
    <style>
      .login-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: calc(100vh - 200px);
      }
      
      .login-card {
        width: 100%;
        max-width: 420px;
        padding: var(--space-8);
      }
      
      .login-header {
        text-align: center;
        margin-bottom: var(--space-6);
      }
      
      .login-logo {
        font-size: 4rem;
        margin-bottom: var(--space-4);
        animation: pulse 2s infinite;
      }
      
      .login-title {
        font-size: var(--text-3xl);
        margin-bottom: var(--space-2);
      }
      
      .login-subtitle {
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
      }
      
      .auth-form.hidden {
        display: none;
      }
    </style>
  `;
}

/**
 * Initialize login view event listeners
 */
export function initLoginView() {
  const tabs = document.querySelectorAll('#auth-tabs .tab');
  const signinForm = document.getElementById('signin-form');
  const signupForm = document.getElementById('signup-form');

  // Tab switching
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      if (tab.dataset.tab === 'signin') {
        signinForm.classList.remove('hidden');
        signupForm.classList.add('hidden');
      } else {
        signinForm.classList.add('hidden');
        signupForm.classList.remove('hidden');
      }
    });
  });

  // Language toggle
  const langBtn = document.querySelector('.login-lang-toggle');
  if (langBtn) {
    langBtn.addEventListener('click', (e) => {
      const nextLang = e.currentTarget.dataset.nextLang;
      setLanguage(nextLang);
    });
  }

  signinForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = document.getElementById('signin-email').value;
    const password = document.getElementById('signin-password').value;

    const submitBtn = signinForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = t('app.loading');

    try {
      await signIn(email, password);
      // toast success handled globally or custom
      router.navigate('/');
    } catch (error) {
      console.error('Sign in error:', error);
      toast.error(t('login.error'));
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = t('login.button');
    }
  });

  // Sign Up
  signupForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = document.getElementById('signup-name').value;
    const email = document.getElementById('signup-email').value;
    const password = document.getElementById('signup-password').value;
    const confirm = document.getElementById('signup-confirm').value;

    if (password !== confirm) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    const submitBtn = signupForm.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Hesap oluşturuluyor...';

    try {
      await signUp(email, password, name);
      toast.success('Hesap başarıyla oluşturuldu!');
      router.navigate('/');
    } catch (error) {
      console.error('Sign up error:', error);
      toast.error(getAuthErrorMessage(error.code));
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Hesap Oluştur';
    }
  });
}

/**
 * Get user-friendly error message
 * @param {string} code - Firebase error code
 * @returns {string}
 */
function getAuthErrorMessage(code) {
  const messages = {
    'auth/email-already-in-use': 'This email is already registered',
    'auth/invalid-email': 'Invalid email address',
    'auth/user-not-found': 'No account found with this email',
    'auth/wrong-password': 'Incorrect password',
    'auth/weak-password': 'Password is too weak',
    'auth/too-many-requests': 'Too many attempts. Please try again later',
    'auth/network-request-failed': 'Network error. Check your connection'
  };
  return messages[code] || 'An error occurred. Please try again.';
}
